import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useMe } from '@/api/queries/auth';
import { pausarAvisosDoAparelho, useAvisosDoAparelho } from '@/api/queries/push';
import { encerrarSessao } from '@/lib/sessao';
import { AvisoDaNavegacao } from '@/components/AvisoDaNavegacao';
import { Button } from '@/components/Button';
import { CloseIcon, LogoutIcon, MenuIcon } from '@/components/icons';
import { LogoNiteroi } from '@/components/LogoNiteroi';
import { cn } from '@/lib/cn';
import { ACESSO_DADOS, AVISOS_TEAMS, LAYOUT, METAS } from '@/lib/glossario';
import { APP_VERSION } from '@/lib/version';

/**
 * Item do menu no desktop: ocupa a altura toda do cabeçalho, e a página atual ganha um traço
 * laranja na base — o mesmo laranja da faixa do topo, que é a assinatura da marca.
 */
const navItem =
  'relative inline-flex h-full items-center whitespace-nowrap px-2.5 text-sm font-medium text-slate-600 transition-colors ' +
  'after:absolute after:inset-x-2.5 after:bottom-0 after:h-0.5 after:rounded-full after:bg-transparent ' +
  'hover:text-niteroi-800 focus-visible:-outline-offset-2';

const navItemActive = 'text-niteroi-800 after:bg-laranja-500';

/** Item do menu no celular: linha inteira clicável, com o marcador laranja à esquerda. */
const navItemMobile =
  'flex min-h-11 items-center rounded-lg border-l-4 border-transparent px-3 text-sm font-medium text-slate-700 hover:bg-slate-50';

const navItemMobileActive = 'border-laranja-500 bg-niteroi-50 text-niteroi-800';

/**
 * Largura e margens laterais da página. Cabeçalho, conteúdo e rodapé usam a mesma, para o
 * logotipo, o título da página e as tabelas começarem todos na mesma linha vertical.
 */
const container = 'mx-auto w-full max-w-[90rem] px-4 sm:px-6';

/** Iniciais para o círculo ao lado do nome: primeira e última palavra do nome. */
function iniciais(nome: string | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

/** Quem está logado: círculo com as iniciais + nome (aparelhos são compartilhados). */
function Pessoa({ nome, curto }: { nome: string | undefined; curto?: boolean }) {
  const primeiroNome = (nome ?? '').trim().split(/\s+/)[0] ?? '';
  return (
    <span className="flex min-w-0 items-center gap-2" title={nome}>
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-niteroi-50 text-xs font-semibold text-niteroi-800"
      >
        {iniciais(nome)}
      </span>
      {curto ? (
        <>
          {/* Só o primeiro nome até 2xl: o nome completo não cabe ao lado dos seis itens. */}
          <span className="max-w-[7rem] truncate text-sm text-slate-700 2xl:hidden">
            {primeiroNome}
          </span>
          <span className="hidden max-w-[14rem] truncate text-sm text-slate-700 2xl:inline">
            {nome}
          </span>
        </>
      ) : (
        <span className="truncate text-sm text-slate-700">{nome}</span>
      )}
    </span>
  );
}

export default function AppLayout() {
  const { data: me } = useMe();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  // Se os avisos estão ligados neste navegador, passam a ser de quem acabou de entrar.
  useAvisosDoAparelho();

  // Trocou de página (clique num item, voltar do navegador): o menu do celular fecha.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // `Esc` fecha o menu do celular, como nos modais.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  async function logout() {
    await pausarAvisosDoAparelho();
    encerrarSessao();
  }

  // Itens do menu — renderizados tanto na navegação desktop quanto no menu mobile.
  // As duas primeiras são as telas de trabalho (lista e metas); o divisor separa o resto.
  const links = [
    { to: '/', end: true, label: 'Processos' },
    { to: '/metas', label: METAS.navItem },
    { to: '/perfil', label: 'Perfil' },
    { to: '/tokens', label: ACESSO_DADOS.navItem },
    { to: '/notificacoes', label: AVISOS_TEAMS.navItem },
    ...(me?.admin ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-niteroi-800 focus:shadow-cartao-alto"
      >
        {LAYOUT.pularParaConteudo}
      </a>

      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        {/* Faixa de acento - laranja institucional de Niterói */}
        <div className="h-1 w-full bg-laranja-500" />
        <div className={cn(container, 'flex h-16 items-center gap-4')}>
          <Link to="/" className="flex min-w-0 shrink-0 items-center gap-3 rounded-lg">
            <LogoNiteroi className="h-7 sm:h-8" />
            <span
              translate="no"
              className="border-l border-slate-200 pl-3 text-sm font-semibold leading-tight text-niteroi-800 sm:text-[0.9375rem]"
            >
              Acompanhamento
              <br />
              Estratégico
            </span>
          </Link>

          {/* Navegação desktop (xl+): abaixo disso os seis itens não cabem numa linha só. */}
          <nav
            aria-label={LAYOUT.menuPrincipal}
            className="ml-2 hidden h-full flex-1 items-stretch xl:flex"
          >
            {links.map((l, i) => (
              <span key={l.to} className="flex items-center">
                {i === 2 && <span className="mx-2 h-5 w-px bg-slate-200" aria-hidden="true" />}
                <NavLink
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) => cn(navItem, isActive && navItemActive)}
                >
                  {l.label}
                </NavLink>
              </span>
            ))}
          </nav>

          <div className="hidden min-w-0 items-center gap-2 xl:flex">
            <Pessoa nome={me?.nomeCompleto} curto />
            <Button variant="ghost" size="sm" onClick={logout} icon={<LogoutIcon />}>
              Sair
            </Button>
          </div>

          {/* Botão do menu (abaixo de xl) */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
            aria-controls="menu-mobile"
            className="ml-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 xl:hidden"
          >
            {menuOpen ? <CloseIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>

        {/* Menu mobile (abaixo de xl) */}
        {menuOpen && (
          <nav
            id="menu-mobile"
            aria-label={LAYOUT.menuPrincipal}
            className={cn(
              container,
              'flex flex-col gap-1 border-t border-slate-200 py-3 xl:hidden',
            )}
          >
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cn(navItemMobile, i === 2 && 'mt-2', isActive && navItemMobileActive)
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
              <Pessoa nome={me?.nomeCompleto} />
              <Button variant="secondary" size="sm" onClick={logout} icon={<LogoutIcon />}>
                Sair
              </Button>
            </div>
          </nav>
        )}
      </header>

      <main
        id="conteudo"
        tabIndex={-1}
        className={cn(container, 'flex-1 py-6 outline-none sm:py-8')}
      >
        <AvisoDaNavegacao />
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div
          className={cn(
            container,
            'flex flex-col gap-1 py-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between',
          )}
        >
          <p>Secretaria executiva - Subsecretaria de Transformação Digital</p>
          <p className="tabular-nums">v{APP_VERSION}</p>
        </div>
      </footer>
    </div>
  );
}
